import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { UserPlus, Loader2, Check, MessageSquare, Mail } from "lucide-react";
import { toast } from "sonner";

/** Admin · Get pinged the second a new user joins iSpeedToClose.
 *  Fires on `POST /api/auth/register`. Same shape as OfferNotificationsCard. */
export default function SignupNotificationsCard() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localPhone, setLocalPhone] = useState("");

  useEffect(() => {
    api.get("/notification-prefs")
      .then((r) => {
        setPrefs(r.data);
        // Fall back to the offer phone if signup phone isn't set yet — most
        // operators use one phone for both channels.
        setLocalPhone(r.data?.notify_signup_phone || r.data?.notify_offer_phone || "");
      })
      .catch(() => toast.error("Couldn't load signup notification prefs"));
  }, []);

  const patch = async (updates) => {
    const prev = prefs;
    const next = { ...prefs, ...updates };
    setPrefs(next);
    setSaving(true);
    try {
      const { data } = await api.patch("/notification-prefs", updates);
      setPrefs(data);
    } catch (err) {
      setPrefs(prev);
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const savePhone = async () => {
    const trimmed = (localPhone || "").trim();
    if (trimmed && trimmed !== (prefs?.notify_signup_phone || "")) {
      await patch({ notify_signup_phone: trimmed });
      toast.success("SMS number saved");
    } else if (!trimmed && prefs?.notify_signup_phone) {
      await patch({ notify_signup_phone: "" });
    }
  };

  if (!prefs) {
    return (
      <div className="card" data-testid="settings-signup-notifications-card">
        <div className="label-eyebrow flex items-center gap-1.5 mb-3"><UserPlus size={11} /> New signup alerts</div>
        <div className="text-sm text-[var(--ink-3)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="settings-signup-notifications-card">
      <div className="label-eyebrow flex items-center gap-1.5 mb-3">
        <UserPlus size={11} /> New signup alerts
      </div>
      <p className="text-xs text-[var(--ink-3)] mb-3">
        Get pinged the second a new user joins iSpeedToClose. Sends attribution (affiliate ref code, UTM source, or "direct") so you know which channel is producing.
      </p>

      <div className="divide-y divide-[var(--line)]">
        <label className="flex items-start justify-between gap-3 py-2 cursor-pointer" data-testid="signup-notify-email-row">
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Mail size={12} /> Email me</div>
            <div className="text-xs text-[var(--ink-3)] mt-0.5">Name, email, trial type, referral/UTM source, and a one-click link to their admin profile.</div>
          </div>
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 accent-[var(--primary)] cursor-pointer"
            checked={!!prefs.notify_signup_email}
            onChange={(e) => patch({ notify_signup_email: e.target.checked })}
            data-testid="signup-notify-email-toggle"
          />
        </label>

        <label className="flex items-start justify-between gap-3 py-2 cursor-pointer" data-testid="signup-notify-sms-row">
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5"><MessageSquare size={12} /> Text me too</div>
            <div className="text-xs text-[var(--ink-3)] mt-0.5">A short SMS ping. Uses your offer SMS number if you don't set a separate one below.</div>
          </div>
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 accent-[var(--primary)] cursor-pointer"
            checked={!!prefs.notify_signup_sms}
            onChange={(e) => patch({ notify_signup_sms: e.target.checked })}
            data-testid="signup-notify-sms-toggle"
          />
        </label>
      </div>

      {prefs.notify_signup_sms && (
        <div className="mt-3 pt-3 border-t border-[var(--line)]" data-testid="signup-notify-sms-config">
          <label htmlFor="signup-notify-phone" className="text-xs text-[var(--ink-3)] mb-1 block">
            SMS number (E.164, e.g. +12155550101) · leave blank to reuse offer number
          </label>
          <div className="flex items-center gap-2">
            <input
              id="signup-notify-phone"
              type="tel"
              value={localPhone}
              onChange={(e) => setLocalPhone(e.target.value)}
              onBlur={savePhone}
              placeholder={prefs.notify_offer_phone || "+12155550101"}
              className="input flex-1"
              maxLength={20}
              data-testid="signup-notify-phone-input"
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--line)]">
        <div className="text-xs text-[var(--ink-3)] flex items-center gap-1">
          {saving
            ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
            : <><Check size={12} className="text-emerald-600" /> Auto-saved</>}
        </div>
      </div>
    </div>
  );
}
